/**
 * Sizing and overflow detection for the company logo script (Pacifico) fallback tile.
 * DOM measurement loop lives in the component; these helpers stay pure and unit-tested.
 */

/** How many lines the name may use before ellipsis (line clamp in CSS). */
export function scriptNameLineClamp(sizePx: number): number {
  if (sizePx < 30) return 2;
  if (sizePx < 56) return 3;
  return 4;
}

/** Minimum font size (px) for readability at a given tile size. */
export function computeCompanyLogoScriptFontFloor(sizePx: number): number {
  if (sizePx < 28) return 6;
  if (sizePx < 36) return 7;
  if (sizePx < 48) return 8;
  return 9;
}

/**
 * Initial guess for script font size from tile geometry (before DOM fit pass).
 */
export function computeCompanyLogoScriptFontPxInitial(
  sizePx: number,
  hasForm: boolean,
  lineCount: number
): number {
  const verticalPad = 6;
  const formReserve = hasForm ? Math.max(11, Math.round(sizePx * 0.2)) : 0;
  const nameBand = Math.max(sizePx - formReserve - verticalPad, sizePx * 0.34);
  const lineHeight = 1.14;
  const fromHeight = nameBand / (lineCount * lineHeight);
  const cap = sizePx * 0.21;
  const floor = computeCompanyLogoScriptFontFloor(sizePx);
  return Math.round(Math.max(floor, Math.min(fromHeight, cap)));
}

export type CompanyLogoScriptLayoutMetrics = {
  scrollHeight: number;
  clientHeight: number;
  scrollWidth: number;
  clientWidth: number;
  offsetHeight: number;
  bandClientHeight: number;
};

/**
 * True if the name element overflows its line-clamp box, its horizontal box, or the flex name band.
 */
export function companyLogoScriptFontHasLayoutOverflow(
  m: CompanyLogoScriptLayoutMetrics,
  epsilon = 1
): boolean {
  const { scrollHeight, clientHeight, scrollWidth, clientWidth, offsetHeight, bandClientHeight } = m;
  return (
    scrollHeight > clientHeight + epsilon ||
    scrollWidth > clientWidth + epsilon ||
    offsetHeight > bandClientHeight + epsilon
  );
}

/**
 * Step down from startPx until layout fits or floor is reached.
 * `applyFontPx` must set font size on the measured element and force layout before returning metrics.
 */
export function fitCompanyLogoScriptFontPx(
  startPx: number,
  floorPx: number,
  applyFontPx: (px: number) => CompanyLogoScriptLayoutMetrics
): number {
  let px = Math.round(startPx);
  const floor = Math.round(floorPx);
  if (px <= floor) {
    applyFontPx(floor);
    return floor;
  }
  for (; px > floor; px -= 1) {
    const m = applyFontPx(px);
    if (!companyLogoScriptFontHasLayoutOverflow(m)) {
      return px;
    }
  }
  applyFontPx(floor);
  return floor;
}
